import { Booking } from '../../core/models/booking';
import { UpcomingSessionsForHealerDTO } from '../../infra/adapters/luminara/booking/dto/upcoming-sessions-dto';
import { extractMessageFromError } from '../../infra/adapters/luminara/common/ErrorUtil';
import { isAccessDenied, isNotFound } from '../../util/auth-util';
import { MusicSelectionPresentation, canStartSession } from '../selectors/healer-dashboard.selector';

export interface HealerDashboardState {
  showLoader: boolean;
  firstUpcomingSession?: Booking;
  remainingUpcomingSessions: Booking[];
  hasUpcomingSessions: boolean;
  dataLoadErrors: string[];
  canStartSession: boolean;
  showMusicSelection: boolean;
  musicSelectionPresentation: MusicSelectionPresentation;
}

export const initialHealerDashboardState = () => {
  return {
    showLoader: true,
    dataLoadErrors: [],
    canStartSession: false,
    hasUpcomingSessions: false,
    showMusicSelection: false,
    remainingUpcomingSessions: [],
    firstUpcomingSession: undefined,
    musicSelectionPresentation: {
      soundtrackOptions: [],
      isPlaying: false,
      selectedSoundtrack: undefined,
      showSoundtrackVolumeLevelBar: false,
    },
  } as HealerDashboardState;
};
export interface HealerDashboardAction {
  type:
    | 'LOAD_UPCOMING_SESSIONS_SUCCESS'
    | 'LOAD_UPCOMING_SESSIONS_FAILURE'
    | 'SET_SHOW_MUSIC_SELECTION'
    | 'SET_DATA_LOADING_ERROR'
    | 'MARK_FIRST_UPCOMING_SESSION_AS_STARTED'
    | 'LOAD_SOUNDTRACK_OPTIONS_SUCCESS'
    | 'LOAD_SOUNDTRACK_OPTIONS_FAILURE'
    | 'SET_SELECTED_SOUNDTRACK_FOR_SESSION'
    | 'SET_MUSIC_PREVIEW_PLAYING'
    | 'BOOKING_CANCELLED_BY_HEALER_FAILURE'
    | 'HEALER_LOGGED_OUT';
  payload?: any;
}

const healerDashboardReducer = (state: HealerDashboardState = initialHealerDashboardState(), action: HealerDashboardAction): HealerDashboardState => {
  switch (action.type) {
    case 'LOAD_UPCOMING_SESSIONS_SUCCESS': {
      const payload: UpcomingSessionsForHealerDTO = action.payload as UpcomingSessionsForHealerDTO;
      const firstUpcomingSession = payload.upcomingBookings.at(0);
      return {
        ...state,
        showLoader: false,
        hasUpcomingSessions: true,
        firstUpcomingSession: firstUpcomingSession,
        remainingUpcomingSessions: payload.upcomingBookings?.length > 1 ? payload?.upcomingBookings.slice(1) : [],
        dataLoadErrors: [],
        canStartSession: canStartSession(firstUpcomingSession),
      };
    }
    case 'LOAD_UPCOMING_SESSIONS_FAILURE': {
      if (isAccessDenied(action.payload) || isNotFound(action.payload)) {
        return {
          ...state,
          showLoader: false,
          hasUpcomingSessions: false,
          firstUpcomingSession: undefined,
          remainingUpcomingSessions: [],
          canStartSession: false,
          showMusicSelection: false,
          dataLoadErrors: [],
        };
      } else {
        return {
          ...state,
          dataLoadErrors: state.dataLoadErrors.concat(extractMessageFromError(action.payload)),
          firstUpcomingSession: undefined,
          remainingUpcomingSessions: [],
          canStartSession: false,
        };
      }
    }
    case 'LOAD_SOUNDTRACK_OPTIONS_SUCCESS': {
      return {
        ...state,
        musicSelectionPresentation: {
          ...state.musicSelectionPresentation,
          soundtrackOptions: action.payload,
        },
      };
    }
    case 'LOAD_SOUNDTRACK_OPTIONS_FAILURE': {
      return {
        ...state,
        musicSelectionPresentation: { isPlaying: false, soundtrackOptions: [], selectedSoundtrack: undefined, showSoundtrackVolumeLevelBar: false },
        dataLoadErrors: state.dataLoadErrors.concat(extractMessageFromError(action.payload)),
      };
    }
    case 'SET_DATA_LOADING_ERROR': {
      return { ...state, dataLoadErrors: state.dataLoadErrors.concat(action.payload) };
    }
    case 'SET_SHOW_MUSIC_SELECTION': {
      return { ...state, showMusicSelection: action.payload };
    }
    case 'SET_SELECTED_SOUNDTRACK_FOR_SESSION': {
      return {
        ...state,
        musicSelectionPresentation: {
          ...state.musicSelectionPresentation,
          selectedSoundtrack: action.payload,
          isPlaying: action.payload == null ? false : state.musicSelectionPresentation.isPlaying,
          showSoundtrackVolumeLevelBar: action.payload != null,
        },
      };
    }
    case 'SET_MUSIC_PREVIEW_PLAYING': {
      return {
        ...state,
        musicSelectionPresentation: {
          ...state.musicSelectionPresentation,
          isPlaying: action.payload === true ? state.musicSelectionPresentation.selectedSoundtrack !== undefined : false,
        },
      };
    }
    case 'MARK_FIRST_UPCOMING_SESSION_AS_STARTED': {
      return { ...state, firstUpcomingSession: { ...(state.firstUpcomingSession as Booking), sessionStatus: 'STARTED' }, showMusicSelection: false };
    }
    case 'BOOKING_CANCELLED_BY_HEALER_FAILURE': {
      return { ...state, dataLoadErrors: state.dataLoadErrors.concat(extractMessageFromError(action.payload)) };
    }
    case 'HEALER_LOGGED_OUT': {
      return initialHealerDashboardState();
    }
    default:
      return state;
  }
};

export default healerDashboardReducer;
